import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@/hooks/global-tools';
import useRelatedApprovals from '@/hooks/approval/useRelatedApprovals';
import type { ApprovalFormValues } from '@/components/Approval/ApprovalCreate/types';
import { Body2 } from '@/components/@IntranetLibrary/Typography';
import RelatedApprovalSection from './RelatedApprovalSection';
import { Root } from './styles';
import type { RelatedApprovalItem, RelatedApprovalType } from './types';

type RelatedApprovalProps = {
  /**
   * 저장된 문서번호(편집 진입 시 기존 docId, 신규 작성은 첫 저장 후 서버 docId).
   * 초기 fetch 는 편집 진입값으로 고정하지만, 자기 문서 검색 제외는 첫 저장으로 docId 가 생긴 시점부터 적용해야 하므로 live 값을 그대로 쓴다.
   */
  documentId?: string;
};

const RelatedApproval = ({ documentId }: RelatedApprovalProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<ApprovalFormValues>();
  const toast = useSnackbar();
  const relatedApprovals = watch('relatedApprovals');
  const deletedRelatedDocIds = watch('deletedRelatedDocIds');
  const initialRelatedDocIds = watch('initialRelatedDocIds');

  // 편집 진입(마운트 시점에 documentId 존재)에만 서버 초기 로드 — 작성 세션 중 첫 저장으로
  // docId가 생긴 경우는 폼 state가 최신이고 서버 read는 생성 직후라 stale이므로 덮으면 안 된다.
  const editEntryDocId = useRef(documentId).current;
  const { parents, children, related, isLoading } = useRelatedApprovals(
    editEntryDocId ?? null,
  );
  const hydratedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!editEntryDocId || isLoading) return;
    if (hydratedForRef.current === editEntryDocId) return;
    const allItems = [...parents, ...children, ...related];
    setValue(
      'relatedApprovals',
      { parents, children, related },
      { shouldDirty: false },
    );
    setValue(
      'initialRelatedDocIds',
      allItems.map((it) => it.docId),
      { shouldDirty: false },
    );
    setValue('deletedRelatedDocIds', [], { shouldDirty: false });
    hydratedForRef.current = editEntryDocId;
  }, [editEntryDocId, isLoading, parents, children, related, setValue]);

  // 카테고리 간 중복 선택 차단용.
  // 주의: deps 에 relatedApprovals 객체를 두면 RHF nested setValue 가 부모 ref 를 유지해 재계산 안 됨 — 내부 배열 ref 로 분해.
  const allSelectedDocIds = useMemo(() => {
    const set = new Set<string>();
    relatedApprovals.parents.forEach((i) => set.add(i.docId));
    relatedApprovals.children.forEach((i) => set.add(i.docId));
    relatedApprovals.related.forEach((i) => set.add(i.docId));
    return set;
  }, [
    relatedApprovals.parents,
    relatedApprovals.children,
    relatedApprovals.related,
  ]);

  const handleAdd = (type: RelatedApprovalType, item: RelatedApprovalItem) => {
    if (allSelectedDocIds.has(item.docId)) {
      toast?.show({ message: t('이미 선택된 품의입니다.'), severity: 'info' });
      return;
    }
    setValue(`relatedApprovals.${type}`, [...relatedApprovals[type], item], {
      shouldDirty: true,
    });
    // 한 번 삭제됐던 항목을 다시 추가하는 경우 deletedRelatedDocIds에서 차감 (재선택 복구)
    if (deletedRelatedDocIds.includes(item.docId)) {
      setValue(
        'deletedRelatedDocIds',
        deletedRelatedDocIds.filter((id) => id !== item.docId),
        { shouldDirty: true },
      );
    }
  };

  const handleRemove = (type: RelatedApprovalType, docId: string) => {
    setValue(
      `relatedApprovals.${type}`,
      relatedApprovals[type].filter((i) => i.docId !== docId),
      { shouldDirty: true },
    );
    // 편집 모드 + 원래 서버에 있던 항목이면 deletedRelatedDocIds에 누적
    if (
      initialRelatedDocIds.includes(docId) &&
      !deletedRelatedDocIds.includes(docId)
    ) {
      setValue('deletedRelatedDocIds', [...deletedRelatedDocIds, docId], {
        shouldDirty: true,
      });
    }
  };

  return (
    <Root>
      <Body2>
        {t(
          '작성중인 품의와 관련된 품의를 연결해 주세요. 품의 상신 후에도 결재라인에 있는 사람이라면 품의를 연결할 수 있어요.',
        )}
      </Body2>
      <RelatedApprovalSection
        type="parents"
        selectedItems={relatedApprovals.parents}
        globalSelectedDocIds={allSelectedDocIds}
        currentDocumentId={documentId}
        onAdd={(item) => handleAdd('parents', item)}
        onRemove={(docId) => handleRemove('parents', docId)}
      />
      <RelatedApprovalSection
        type="children"
        selectedItems={relatedApprovals.children}
        globalSelectedDocIds={allSelectedDocIds}
        currentDocumentId={documentId}
        onAdd={(item) => handleAdd('children', item)}
        onRemove={(docId) => handleRemove('children', docId)}
      />
      <RelatedApprovalSection
        type="related"
        selectedItems={relatedApprovals.related}
        globalSelectedDocIds={allSelectedDocIds}
        currentDocumentId={documentId}
        onAdd={(item) => handleAdd('related', item)}
        onRemove={(docId) => handleRemove('related', docId)}
      />
    </Root>
  );
};

export default RelatedApproval;
